import React, { useState, useEffect } from 'react'
import { Brain, Image, Zap } from 'lucide-react'

const APIStatusPanel = ({ apiService }) => {
  const [healthStatus, setHealthStatus] = useState({})
  const [loading, setLoading] = useState(true)

  const apis = [
    {
      name: 'OpenAI',
      description: 'Content generation and analysis (via Cloud Functions)',
      icon: Brain,
      key: 'openai',
      url: 'https://platform.openai.com/api-keys',
      envVar: 'OPENAI_API_KEY (Cloud Function)',
      features: ['Text Generation', 'Content Analysis', 'Secure Server-Side']
    },
    {
      name: 'Google AI',
      description: 'High-quality AI image generation (via Cloud Functions)',
      icon: Image,
      key: 'google',
      url: 'https://ai.google.dev/',
      envVar: 'GOOGLE_API_KEY (Cloud Function)',
      features: ['AI Generation', 'Text Support', 'High Quality', 'Secure Server-Side']
    }
  ]

  useEffect(() => {
    const checkHealth = async () => {
      if (apiService) {
        try {
          const status = await apiService.checkAPIHealth()
          setHealthStatus(status)
        } catch (error) {
          console.error('Error checking API health:', error)
        }
      }
      setLoading(false)
    }

    checkHealth()
  }, [apiService])

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-50'
      case 'error':
        return 'text-red-600 bg-red-50'
      case 'not_configured':
        return 'text-yellow-600 bg-yellow-50'
      default:
        return 'text-gray-600 bg-gray-50'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'healthy':
        return 'Working'
      case 'error':
        return 'Error'
      case 'not_configured':
        return 'Not Configured'
      default:
        return 'Unknown'
    }
  }

  const isConfigured = (api) => {
    // With Cloud Functions, we check if the service exists (always true if initialized)
    return apiService !== null && apiService !== undefined
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">API Status</h3>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-primary-600 hover:text-primary-700"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
          <span className="ml-2 text-gray-600">Checking API status...</span>
        </div>
      ) : (
        <div className="space-y-4">
          {apis.map((api) => {
            const Icon = api.icon
            const configured = isConfigured(api)
            const status = healthStatus[api.key] || 'not_configured'
            
            return (
              <div key={api.key} className="flex items-start space-x-4 p-4 border border-gray-100 rounded-lg">
                <div className={`p-2 rounded-lg ${configured ? 'bg-primary-50' : 'bg-gray-50'}`}>
                  <Icon className={`w-5 h-5 ${configured ? 'text-primary-600' : 'text-gray-400'}`} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">{api.name}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(status)}`}>
                      {getStatusText(status)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mt-1">{api.description}</p>
                  
                  <div className="flex items-center space-x-4 mt-2">
                    <div className="flex items-center space-x-1">
                      <div className={`w-2 h-2 rounded-full ${configured ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <span className="text-xs text-gray-500">
                        {configured ? 'Configured' : 'Not configured'}
                      </span>
                    </div>
                    
                    {configured && (
                      <a
                        href={api.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary-600 hover:text-primary-700"
                      >
                        Manage API
                      </a>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mt-2">
                    {api.features.map((feature, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <Zap className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">API Configuration</h4>
            <p className="text-sm text-blue-700">
              Configure your API keys in the <code className="bg-blue-100 px-1 rounded">.env</code> file. 
              See <code className="bg-blue-100 px-1 rounded">API_KEYS_SETUP.md</code> for detailed instructions.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default APIStatusPanel 